import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarCheck, CalendarX2, ChevronLeft, ChevronRight, Info, Loader2, Plus, Trash2 } from 'lucide-react';
import { api } from '@/shared/api/cliente';
import { ErrorDeApi } from '@/shared/api/problema';
import { useSesion } from '@/shared/sesion/SesionProvider';
import { notificar } from '@/shared/ui/avisos';
import { Campo, Seccion, entrada } from './ajustes';
import { diaDeLaSemana, esFinDeSemana, ultimoDiaHabil } from './feriados';

interface Feriado {
  fecha: string;
  nombre: string;
  ambito: 'nacional' | 'local';
}

const MESES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

/**
 * Los feriados que mueven el vencimiento de la tasa mensual: su último día hábil del mes
 * (reglamento de aplicación, Art. 5). No hay calendario cargado de fábrica: los feriados
 * nacionales se trasladan cada año por decreto, y los mantiene EPAGAL aquí.
 */
export function PaginaFeriados() {
  const [anio, setAnio] = useState(new Date().getFullYear());
  const [fecha, setFecha] = useState('');
  const [nombre, setNombre] = useState('');
  const [ambito, setAmbito] = useState<'nacional' | 'local'>('nacional');
  const { puede } = useSesion();
  const puedeEditar = puede('configuracion.editar');
  const clienteDeConsultas = useQueryClient();

  const feriados = useQuery({
    queryKey: ['feriados', anio],
    queryFn: () => api.get<Feriado[]>('/feriados', { query: { anio } }),
  });

  const refrescar = () => void clienteDeConsultas.invalidateQueries({ queryKey: ['feriados'] });

  const cargar = useMutation({
    mutationFn: () => api.post<Feriado>('/feriados', { fecha, nombre: nombre.trim(), ambito }),
    onSuccess: (f) => {
      notificar.exito(`${f.fecha} · ${f.nombre}`, 'Feriado cargado');
      setFecha('');
      setNombre('');
      refrescar();
    },
    onError: (causa) =>
      notificar.error(causa instanceof ErrorDeApi ? causa.message : 'NO SE PUDO CARGAR EL FERIADO'),
  });

  const quitar = useMutation({
    mutationFn: (f: Feriado) => api.delete<void>(`/feriados/${f.fecha}`),
    onSuccess: () => {
      notificar.exito('Feriado quitado');
      refrescar();
    },
    onError: (causa) =>
      notificar.error(causa instanceof ErrorDeApi ? causa.message : 'NO SE PUDO QUITAR'),
  });

  const lista = feriados.data ?? [];
  const fechas = new Set(lista.map((f) => f.fecha));
  const faltaAlgo = fecha === '' || nombre.trim() === '';
  const cae = fecha !== '' && esFinDeSemana(fecha);

  return (
    <div className="grid gap-6">
      <div className="flex gap-3 rounded-xl border border-blue-200 bg-blue-50 p-4">
        <Info className="mt-0.5 size-4 shrink-0 text-blue-600" aria-hidden />
        <p className="text-xs uppercase leading-relaxed text-blue-800">
          <span className="font-semibold">La tasa mensual vence el último día hábil del mes.</span>{' '}
          No son hábiles los sábados, los domingos ni los feriados nacionales y locales de
          Latacunga que se carguen aquí. Si un feriado se traslada, se carga el día al que se movió.
          Los generadores no cambian: tienen 30 días corridos desde la emisión.
        </p>
      </div>

      <Seccion
        icono={CalendarX2}
        color="ambar"
        titulo={`Feriados de ${anio}`}
        accion={
          <span className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setAnio(anio - 1)}
              aria-label="Año anterior"
              className="grid size-8 place-items-center rounded-lg border border-armazon-200 bg-white text-armazon-600 hover:bg-armazon-50"
            >
              <ChevronLeft className="size-4" aria-hidden />
            </button>
            <span className="importe w-14 text-center text-sm font-bold text-armazon-800">{anio}</span>
            <button
              type="button"
              onClick={() => setAnio(anio + 1)}
              aria-label="Año siguiente"
              className="grid size-8 place-items-center rounded-lg border border-armazon-200 bg-white text-armazon-600 hover:bg-armazon-50"
            >
              <ChevronRight className="size-4" aria-hidden />
            </button>
          </span>
        }
      >
        {feriados.isPending ? (
          <p className="flex items-center gap-2 text-xs uppercase text-armazon-500">
            <Loader2 className="size-3.5 animate-spin" aria-hidden /> Cargando…
          </p>
        ) : lista.length === 0 ? (
          <p className="text-xs uppercase text-armazon-500">
            No hay feriados cargados para {anio}: solo cuentan sábados y domingos.
          </p>
        ) : (
          <ul className="divide-y divide-armazon-100">
            {lista.map((f) => (
              <li key={f.fecha} className="flex items-center gap-4 py-2.5">
                <span className="importe w-24 text-sm font-semibold text-armazon-800">{f.fecha}</span>
                <span className="w-20 text-[11px] uppercase text-armazon-500">
                  {diaDeLaSemana(f.fecha)}
                </span>
                <span className="min-w-0 flex-1 truncate text-sm uppercase text-armazon-700">
                  {f.nombre}
                </span>
                <span
                  className={`rounded-md px-2 py-0.5 text-[10px] font-bold uppercase ${
                    f.ambito === 'local' ? 'bg-violet-50 text-violet-700' : 'bg-blue-50 text-blue-700'
                  }`}
                >
                  {f.ambito === 'local' ? 'Local' : 'Nacional'}
                </span>
                {puedeEditar && (
                  <button
                    type="button"
                    onClick={() => quitar.mutate(f)}
                    disabled={quitar.isPending}
                    aria-label={`Quitar el feriado del ${f.fecha}`}
                    className="grid size-8 place-items-center rounded-lg text-armazon-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                  >
                    <Trash2 className="size-4" aria-hidden />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}

        {puedeEditar && (
          <div className="mt-5 grid gap-4 border-t border-armazon-100 pt-5 sm:grid-cols-[10rem_1fr_10rem_auto] sm:items-end">
            <Campo etiqueta="Fecha">
              <input
                type="date"
                value={fecha}
                onChange={(e) => setFecha(e.target.value)}
                className={entrada}
              />
            </Campo>
            <Campo etiqueta="Qué se celebra">
              <input
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                maxLength={120}
                placeholder="Independencia de Latacunga"
                className={entrada}
              />
            </Campo>
            <Campo etiqueta="Ámbito">
              <select
                value={ambito}
                onChange={(e) => setAmbito(e.target.value as 'nacional' | 'local')}
                className={entrada}
              >
                <option value="nacional">Nacional</option>
                <option value="local">Local · Latacunga</option>
              </select>
            </Campo>
            <button
              type="button"
              onClick={() => cargar.mutate()}
              disabled={faltaAlgo || cae || cargar.isPending}
              className="inline-flex h-11 items-center justify-center gap-2 rounded-lg bg-marca-600 px-4 text-xs font-bold uppercase text-white hover:bg-marca-700 disabled:opacity-50"
            >
              {cargar.isPending ? (
                <Loader2 className="size-4 animate-spin" aria-hidden />
              ) : (
                <Plus className="size-4" aria-hidden />
              )}
              Cargar
            </button>
            {cae && (
              <p className="text-[11px] uppercase text-amber-700 sm:col-span-4">
                Ese día ya es fin de semana: no hace falta cargarlo.
              </p>
            )}
          </div>
        )}
      </Seccion>

      <Seccion icono={CalendarCheck} color="marca" titulo={`Así vence la tasa mensual en ${anio}`}>
        <p className="mb-4 text-[11px] uppercase text-armazon-500">
          El último día hábil de cada mes con los feriados de arriba: hasta ese día se paga sin
          interés la factura que se emitió en ese mes.
        </p>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
          {MESES.map((mes, i) => {
            const vence = ultimoDiaHabil(anio, i + 1, fechas);
            const ultimo = new Date(Date.UTC(anio, i + 1, 0)).toISOString().slice(0, 10);
            return (
              <div
                key={mes}
                className={`rounded-lg border px-3 py-2 ${
                  vence === ultimo ? 'border-armazon-200 bg-white' : 'border-amber-200 bg-amber-50/60'
                }`}
              >
                <span className="block text-[10px] font-semibold uppercase text-armazon-500">{mes}</span>
                <span className="importe block text-sm font-bold text-armazon-800">{vence}</span>
                <span className="block text-[10px] uppercase text-armazon-400">
                  {diaDeLaSemana(vence)}
                </span>
              </div>
            );
          })}
        </div>
      </Seccion>
    </div>
  );
}
